import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  ChartBarIcon,
  HeartIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  AcademicCapIcon,
  ShieldCheckIcon,
  BuildingOffice2Icon,
  CogIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { collection, doc, setDoc, getDoc, onSnapshot } from 'firebase/firestore';
import { firestore } from '../../database/firebase';

import ProgramHealthScoreCard from './ProgramHealthScoreCard';
import ProgramDesignAssessment from './ProgramDesignAssessment';
import EntrepreneurProfilingAssessment from './EntrepreneurProfilingAssessment';
import CurriculumDeliveryAssessment from './CurriculumDeliveryAssessment';
import MonitoringEvaluationAssessment from './MonitoringEvaluationAssessment';
import SupportEcosystemAssessment from './SupportEcosystemAssessment';
import GovernanceInclusionAssessment from './GovernanceInclusionAssessment';

const HEALTH_CHECK_COLLECTION = 'programHealthChecks';

const ProgramHealthCheckDashboard = ({ userRole = 'admin', programId = null }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [healthCheckData, setHealthCheckData] = useState({
    programDesign: {},
    entrepreneurProfiling: {},
    curriculumDelivery: {},
    monitoringEvaluation: {},
    supportEcosystem: {},
    governanceInclusion: {},
    overallScore: 0,
    lastUpdated: null,
  });
  const [allProgramsData, setAllProgramsData] = useState([]);
  const [analytics, setAnalytics] = useState({});

  const canViewAllPrograms = userRole === 'admin' || userRole === 'moderator';

  useEffect(() => {
    setLoading(true);

    if (canViewAllPrograms) {
      const healthChecksRef = collection(firestore, HEALTH_CHECK_COLLECTION);
      const unsubscribe = onSnapshot(
        healthChecksRef,
        snapshot => {
          const programsData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          }));
          setAllProgramsData(programsData);

          const analyticsData = calculateAnalytics(programsData);
          setAnalytics(analyticsData);

          setLoading(false);
        },
        err => {
          toast.error('Error loading health check data');
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } else {
      const targetId = programId || 'default';
      const healthCheckRef = doc(firestore, HEALTH_CHECK_COLLECTION, targetId);

      const unsubscribe = onSnapshot(
        healthCheckRef,
        docSnap => {
          if (docSnap.exists()) {
            setHealthCheckData(docSnap.data());
          } else {
            const emptyData = {
              programDesign: {},
              entrepreneurProfiling: {},
              curriculumDelivery: {},
              monitoringEvaluation: {},
              supportEcosystem: {},
              governanceInclusion: {},
              overallScore: 0,
              lastUpdated: null,
            };
            setHealthCheckData(emptyData);
          }
          setLoading(false);
        },
        err => {
          toast.error('Error loading health check data');
          const emptyData = {
            programDesign: {},
            entrepreneurProfiling: {},
            curriculumDelivery: {},
            monitoringEvaluation: {},
            supportEcosystem: {},
            governanceInclusion: {},
            overallScore: 0,
            lastUpdated: null,
          };
          setHealthCheckData(emptyData);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    }
  }, [programId, canViewAllPrograms]);

  const calculateAnalytics = programsData => {
    if (programsData.length === 0) return {};

    const totalPrograms = programsData.length;
    const avgHealthScore =
      programsData.reduce((sum, program) => sum + (program.overallScore || 0), 0) / totalPrograms;

    const healthyPrograms = programsData.filter(p => (p.overallScore || 0) >= 81).length;
    const emergingPrograms = programsData.filter(
      p => (p.overallScore || 0) >= 61 && (p.overallScore || 0) < 81
    ).length;
    const atRiskPrograms = programsData.filter(
      p => (p.overallScore || 0) >= 41 && (p.overallScore || 0) < 61
    ).length;
    const criticalPrograms = programsData.filter(p => (p.overallScore || 0) < 41).length;

    return {
      totalPrograms,
      avgHealthScore: Math.round(avgHealthScore),
      healthyPrograms,
      emergingPrograms,
      atRiskPrograms,
      criticalPrograms,
      healthDistribution: {
        healthy: Math.round((healthyPrograms / totalPrograms) * 100),
        emerging: Math.round((emergingPrograms / totalPrograms) * 100),
        atRisk: Math.round((atRiskPrograms / totalPrograms) * 100),
        critical: Math.round((criticalPrograms / totalPrograms) * 100),
      },
    };
  };

  const handleSaveAssessment = async (category, data) => {
    try {
      const targetId = programId || 'default';
      const healthCheckRef = doc(firestore, HEALTH_CHECK_COLLECTION, targetId);

      const updatedData = {
        ...healthCheckData,
        [category]: data,
        lastUpdated: new Date().toISOString(),
        userRole: userRole,
      };

      updatedData.overallScore = calculateOverallScore(updatedData);

      await setDoc(healthCheckRef, updatedData, { merge: true });
      setHealthCheckData(updatedData);
      toast.success(`${category} assessment saved successfully!`);
    } catch (error) {
      toast.error('Failed to save assessment');
      throw error;
    }
  };

  const calculateOverallScore = data => {
    const categories = [
      'programDesign',
      'entrepreneurProfiling',
      'curriculumDelivery',
      'monitoringEvaluation',
      'supportEcosystem',
      'governanceInclusion',
    ];

    let totalScore = 0;
    let categoryCount = 0;

    categories.forEach(category => {
      if (data[category] && data[category].score !== undefined) {
        totalScore += data[category].score;
        categoryCount++;
      }
    });

    return categoryCount > 0 ? Math.round(totalScore / categoryCount) : 0;
  };

  const getHealthStatus = score => {
    if (score >= 81) return { status: 'Healthy & Scalable', color: 'green', icon: CheckCircleIcon };
    if (score >= 61) return { status: 'Emerging Maturity', color: 'yellow', icon: ClockIcon };
    if (score >= 41) return { status: 'At Risk', color: 'orange', icon: ExclamationTriangleIcon };
    return { status: 'Critical Gaps', color: 'red', icon: XCircleIcon };
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'programDesign', name: 'Program Design & Structure', icon: BuildingOffice2Icon },
    {
      id: 'entrepreneurProfiling',
      name: 'Entrepreneur Profiling & Assessment',
      icon: UserGroupIcon,
    },
    { id: 'curriculumDelivery', name: 'Curriculum & Program Delivery', icon: AcademicCapIcon },
    {
      id: 'monitoringEvaluation',
      name: 'Monitoring & Evaluation',
      icon: ClipboardDocumentListIcon,
    },
    { id: 'supportEcosystem', name: 'Support Ecosystem & Operations', icon: CogIcon },
    { id: 'governanceInclusion', name: 'Governance, Inclusion & Risk', icon: ShieldCheckIcon },
  ];

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading program health check...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <div className="mb-2 flex items-center gap-2 md:gap-3">
            <HeartIcon className="h-8 w-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-black md:text-3xl">
              Program Health Check Framework
            </h1>
          </div>
          <p className="text-sm text-black md:text-base">
            Comprehensive assessment of program design, implementation, and monitoring effectiveness
          </p>
          {!canViewAllPrograms && healthCheckData.overallScore === 0 && (
            <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="text-sm text-blue-800">
                Welcome to the Program Health Check Framework! Start by exploring the assessment
                categories below to evaluate your program's effectiveness.
              </p>
            </div>
          )}
        </div>

        {canViewAllPrograms && (
          <div className="mb-8">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg border border-blue-200 bg-blue-100 p-4 shadow-sm">
                <div className="mb-2 flex items-center gap-2">
                  <ChartBarIcon className="h-5 w-5 text-blue-600" />
                  <span className="text-sm text-black">Total Programs</span>
                </div>
                <p className="text-2xl font-bold text-black">{analytics.totalPrograms || 0}</p>
              </div>
              <div className="rounded-lg border border-green-200 bg-green-100 p-4 shadow-sm">
                <div className="mb-2 flex items-center gap-2">
                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-black">Healthy Programs</span>
                </div>
                <p className="text-2xl font-bold text-black">{analytics.healthyPrograms || 0}</p>
              </div>
              <div className="rounded-lg border border-yellow-200 bg-yellow-100 p-4 shadow-sm">
                <div className="mb-2 flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
                  <span className="text-sm text-black">At Risk Programs</span>
                </div>
                <p className="text-2xl font-bold text-black">{analytics.atRiskPrograms || 0}</p>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-100 p-4 shadow-sm">
                <div className="mb-2 flex items-center gap-2">
                  <XCircleIcon className="h-5 w-5 text-red-600" />
                  <span className="text-sm text-black">Critical Programs</span>
                </div>
                <p className="text-2xl font-bold text-black">{analytics.criticalPrograms || 0}</p>
              </div>
            </div>
            {(!analytics.totalPrograms || analytics.totalPrograms === 0) && (
              <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
                <p className="text-sm text-gray-600">
                  No program health data available yet. Start by completing assessments in the tabs
                  below.
                </p>
              </div>
            )}
          </div>
        )}

        <div className="mb-8 rounded-lg border border-blue-200 bg-white shadow-sm">
          <div className="border-b border-blue-200">
            <nav className="flex flex-wrap gap-2 px-4 md:space-x-8 md:px-6">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-black'
                      : 'border-transparent text-black hover:border-blue-300 hover:text-black'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-4 md:p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {activeTab === 'overview' && (
                  <ProgramHealthScoreCard
                    healthCheckData={healthCheckData}
                    allProgramsData={allProgramsData}
                    analytics={analytics}
                    userRole={userRole}
                    onScoreClick={() => setActiveTab('programDesign')}
                    onCategoryClick={category => setActiveTab(category)}
                  />
                )}

                {activeTab === 'programDesign' && (
                  <ProgramDesignAssessment
                    data={healthCheckData.programDesign}
                    onSave={data => handleSaveAssessment('programDesign', data)}
                    userRole={userRole}
                  />
                )}

                {activeTab === 'entrepreneurProfiling' && (
                  <EntrepreneurProfilingAssessment
                    data={healthCheckData.entrepreneurProfiling}
                    onSave={data => handleSaveAssessment('entrepreneurProfiling', data)}
                    userRole={userRole}
                  />
                )}

                {activeTab === 'curriculumDelivery' && (
                  <CurriculumDeliveryAssessment
                    data={healthCheckData.curriculumDelivery}
                    onSave={data => handleSaveAssessment('curriculumDelivery', data)}
                    userRole={userRole}
                  />
                )}

                {activeTab === 'monitoringEvaluation' && (
                  <MonitoringEvaluationAssessment
                    data={healthCheckData.monitoringEvaluation}
                    onSave={data => handleSaveAssessment('monitoringEvaluation', data)}
                    userRole={userRole}
                  />
                )}

                {activeTab === 'supportEcosystem' && (
                  <SupportEcosystemAssessment
                    data={healthCheckData.supportEcosystem}
                    onSave={data => handleSaveAssessment('supportEcosystem', data)}
                    userRole={userRole}
                  />
                )}

                {activeTab === 'governanceInclusion' && (
                  <GovernanceInclusionAssessment
                    data={healthCheckData.governanceInclusion}
                    onSave={data => handleSaveAssessment('governanceInclusion', data)}
                    userRole={userRole}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramHealthCheckDashboard;
